import React, { useState } from 'react';
import { DatabaseIcon, PlusIcon, PencilIcon, Trash2Icon, SearchIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { deedTypes, landTypes, districts, courtNames, subRegistryOffices } from '../data/seedData';
import { MasterDataItem } from '../data/types';
type TabId = 'deed-types' | 'land-types' | 'districts' | 'courts' | 'sub-registries';
interface Tab {
  id: TabId;
  label: string;
}
const tabs: Tab[] = [{
  id: 'deed-types',
  label: 'Deed Types'
}, {
  id: 'land-types',
  label: 'Land Types'
}, {
  id: 'districts',
  label: 'Districts'
}, {
  id: 'courts',
  label: 'Courts'
}, {
  id: 'sub-registries',
  label: 'Sub-Registries'
}];
export function MasterData() {
  const {
    state
  } = useApp();
  const {
    language
  } = state;
  const [activeTab, setActiveTab] = useState<TabId>('deed-types');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const getData = (): MasterDataItem[] => {
    switch (activeTab) {
      case 'deed-types':
        return deedTypes;
      case 'land-types':
        return landTypes;
      case 'districts':
        return districts;
      case 'courts':
        return courtNames;
      case 'sub-registries':
        return subRegistryOffices;
      default:
        return [];
    }
  };
  const filteredData = getData().filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.nameBn && item.nameBn.includes(searchQuery));
  return <div className="p-4 lg:p-6 space-y-6" data-id="element-904">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-905">
        <div data-id="element-906">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-907">
            {language === 'en' ? 'Master Data' : 'মাস্টার ডেটা'}
          </h1>
          <p className="text-gray-600 mt-1" data-id="element-908">
            {language === 'en' ? 'Manage system reference data' : 'সিস্টেম রেফারেন্স ডেটা পরিচালনা করুন'}
          </p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-909">
          <PlusIcon className="w-5 h-5" data-id="element-910" />
          <span data-id="element-911">Add New</span>
        </button>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col h-[calc(100vh-12rem)]" data-id="element-912">
        {/* Tabs */}
        <div className="border-b border-gray-200 overflow-x-auto" data-id="element-913">
          <div className="flex min-w-max" data-id="element-914">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-blue-600 text-blue-600 bg-blue-50/50' : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-915">
                {tab.label}
              </button>)}
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200 bg-gray-50/50" data-id="element-916">
          <div className="relative max-w-md" data-id="element-917">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-918" />
            <input type="text" placeholder={`Search ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()}...`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-919" />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto" data-id="element-920">
          <table className="w-full text-sm" data-id="element-921">
            <thead data-id="element-922">
              <tr className="bg-gray-50 border-b border-gray-200 sticky top-0" data-id="element-923">
                <th className="text-left px-6 py-3 font-medium text-gray-600" data-id="element-924">
                  Name (English)
                </th>
                <th className="text-left px-6 py-3 font-medium text-gray-600" data-id="element-925">
                  Name (Bangla)
                </th>
                <th className="text-left px-6 py-3 font-medium text-gray-600" data-id="element-926">
                  Status
                </th>
                <th className="text-right px-6 py-3 font-medium text-gray-600" data-id="element-927">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-928">
              {filteredData.map(item => <tr key={item.id} className="hover:bg-gray-50 transition-colors" data-id="element-929">
                  <td className="px-6 py-3 font-medium text-gray-900" data-id="element-930">
                    {item.name}
                  </td>
                  <td className="px-6 py-3 text-gray-600 font-bengali" data-id="element-931">
                    {item.nameBn || '-'}
                  </td>
                  <td className="px-6 py-3" data-id="element-932">
                    {item.status === 'Active' ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700" data-id="element-933">
                        <CheckCircleIcon className="w-3 h-3" data-id="element-934" /> Active
                      </span> : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-935">
                        <XCircleIcon className="w-3 h-3" data-id="element-936" /> Inactive
                      </span>}
                  </td>
                  <td className="px-6 py-3 text-right" data-id="element-937">
                    <div className="flex items-center justify-end gap-2" data-id="element-938">
                      <button className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-939">
                        <PencilIcon className="w-4 h-4" data-id="element-940" />
                      </button>
                      <button className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-941">
                        <Trash2Icon className="w-4 h-4" data-id="element-942" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-943">
          <div className="bg-white rounded-xl w-full max-w-md p-6" data-id="element-944">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-945">
              Add New Item
            </h2>
            <div className="space-y-4" data-id="element-946">
              <div data-id="element-947">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-948">
                  Name (English)
                </label>
                <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-949" />
              </div>
              <div data-id="element-950">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-951">
                  Name (Bangla)
                </label>
                <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-952" />
              </div>
              <div data-id="element-953">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-954">
                  Status
                </label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-955">
                  <option value="Active" data-id="element-956">Active</option>
                  <option value="Inactive" data-id="element-957">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-958">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-959">
                Cancel
              </button>
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-960">
                Save
              </button>
            </div>
          </div>
        </div>}
    </div>;
}